import { Spacing } from '@persona-id/design-tokens';
import { createRef, ReactElement, useEffect } from 'react';
import Slider from 'react-slick';
import { SPACINGS } from 'shared/styles/variables';
import styled, { css, ThemeProvider } from 'styled-components';

import { isFullscreenPage } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import ThemedFonts from 'dashboard/components/ThemedFonts';
import { serializeTheme } from 'dashboard/lib/theme';
import { Theme } from 'dashboard/models/theme';
import { ThemeSet } from 'dashboard/models/theme-set';
import { Elevation } from 'design-system/variables/effect';
import { NAVBAR_ID } from 'inquiry/components/ConnectedNavbar/ConnectedNavbar';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';
import InquiryProgressBar from 'verify/components/InquiryProgressBar';
import Navbar from 'verify/components/Navbar/Navbar';

import 'slick-carousel/slick/slick.css';

/**
 * We vendor this in instead of importing slick-theme.css because
 * 1. The way slick-theme.css imports a SVG in the font declaration breaks
 *    OptimizeCssAssetsWebpackPlugin.
 * 2. We don't need half of the stuff in there (only dots code)
 * 3. This library is barely updated and we're not too worried about desyncing
 */
const vendoredSlickCssTheme = css`
  .slick-dots {
    position: absolute;
    bottom: -25px;

    display: block;

    width: 100%;
    padding: 0;
    margin: 0;

    list-style: none;

    text-align: center;
  }
  .slick-dots li {
    position: relative;

    display: inline-block;

    width: 20px;
    height: 20px;
    margin: 0 5px;
    padding: 0;

    cursor: pointer;
  }
  .slick-dots li button {
    font-size: 0;
    line-height: 0;

    display: block;

    width: 20px;
    height: 20px;
    padding: 5px;

    cursor: pointer;

    color: transparent;
    border: 0;
    outline: none;
    background: transparent;
  }
  .slick-dots li button:hover,
  .slick-dots li button:focus {
    outline: none;
  }
  .slick-dots li button:hover:before,
  .slick-dots li button:focus:before {
    opacity: 1;
  }
  .slick-dots li button:before {
    font-family: 'slick';
    font-size: 6px;
    line-height: 20px;

    position: absolute;
    top: 0;
    left: 0;

    width: 20px;
    height: 20px;

    content: '•';
    text-align: center;

    opacity: 0.25;
    color: black;

    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  .slick-dots li.slick-active button:before {
    opacity: 0.75;
    color: black;
  }
`;

interface ContainerProps {
  hasUrl?: boolean;
}

const Container = styled('div')<ContainerProps>`
  ${vendoredSlickCssTheme};
  position: relative;
  flex: auto;

  ${({ hasUrl, theme }) => hasUrl && `background-color: ${theme.color.background.primary};`};

  // NOTE: the following css limits the size of the .slick-list, which makes react-slick
  // play nicely with browser zoom.
  display: block;
  width: 100%;

  .slick-slider {
    display: flex;
    align-items: center;
    height: 100%;
    flex-direction: column;
    justify-content: center;
    width: 100%;
  }

  .slick-list {
    width: 100%;
  }

  // Note (Evan): For window resizes, allow the slick carousel to transition
  // without an abrupt jump
  .slick-track {
    transition: transform 200ms ease-out;
    min-width: max-content;
  }

  .slick-slide {
    transition: 0.3s;

    :not(.slick-active) {
      opacity: 0.5;
    }
  }

  .slick-dots {
    position: relative;
    bottom: 0;

    li button:before {
      font-family: inherit;
      font-size: 16px;
    }
  }

  // NOTE: the following CSS solves the 'clicked dots remaining active' issue
  // https://github.com/akiran/react-slick/issues/1432
  .slick-dots li button:focus:before {
    opacity: 0.25;
  }
  .slick-dots li.slick-active button:focus:before,
  .slick-dots li button:hover:before {
    opacity: 1;
  }
`;

// NB: Slide must be wrapped before being passed to Slider, else it gets overridden
const Slide = styled('div')<{ isFullscreenPage: boolean; themeSet?: ThemeSet }>`
  position: relative;
  height: ${FRAME_HEIGHT}px;
  width: 400px;

  margin: ${Spacing[6]};

  ${({ isFullscreenPage }) =>
    !isFullscreenPage &&
    css`
      // prettier-ignore
      // 72px taken from implementation of  widgetPadding() in HolderClient
      padding: 72px ${SPACINGS.widgetPadding} ${SPACINGS.widgetPadding} ${SPACINGS.widgetPadding};
    `};

  box-shadow: ${Elevation.High};

  // TODO dont rely on an extra themeing from dashboard, only use what the verify flow uses
  background-color: ${({ theme, themeSet }) =>
    themeSet?.attributes['light-base-theme']['background-color']?.value ??
    theme.colorModalBackground};
  border-radius: ${({ theme, themeSet }) => {
    const themeSetModalBorderRadius =
      themeSet?.attributes['light-inquiry-theme']['modal-border-radius'];
    return themeSetModalBorderRadius
      ? `${themeSetModalBorderRadius.value}${themeSetModalBorderRadius.unit}`
      : theme.borderRadiusModal;
  }};
  transition: 0.3s;
  overflow-y: scroll;

  & > * {
    pointer-events: none;
  }
`;

const SlideProgressBar = styled(InquiryProgressBar)`
  left: 0;
  top: 0;
`;

interface InquiryFlowPreviewProps {
  theme: Theme | null;
  themeSet?: ThemeSet;
  templateId: string;
  pages: ReactElement[];
  settings: { [key: string]: any };
  showProgressBar?: boolean;
  goToSlideNumber?: number;
}

const InquiryFlowPreview = (props: InquiryFlowPreviewProps) => {
  const serializedTheme = serializeTheme(props.theme);

  const totalPages = props.pages.length;

  const refSlick = createRef<Slider>();
  useEffect(() => {
    const goToSlideNumber =
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      props.goToSlideNumber < 0 ? totalPages + props.goToSlideNumber : props.goToSlideNumber;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    refSlick.current.slickGoTo(goToSlideNumber);
  }, [totalPages, props.goToSlideNumber, refSlick]);

  return (
    <ThemeProvider theme={serializedTheme}>
      <Container>
        {/* Note: we add the key here to force a re-render of slick for the new template screens */}
        <Slider {...props.settings} key={props.templateId} ref={refSlick}>
          {props.pages.map((page, key) => {
            const logo = page.props.inquiry?.attributes.logo;
            const progress = (key / (totalPages - 1)) * 100;
            return (
              <ThemedFonts key={key}>
                <Slide isFullscreenPage={isFullscreenPage(page)} themeSet={props.themeSet}>
                  <Navbar
                    id={NAVBAR_ID}
                    data-test='navbar__inquiry-dynamic-flow'
                    onBack={() => {}}
                    onClose={() => {}}
                    showBackButton={false}
                    showCloseButton={false}
                    logoContentType={logo?.['content-type']}
                    logoDisplay={logo?.display}
                    logoUrl={logo?.['url']}
                    loading={false}
                  />
                  {props.showProgressBar && <SlideProgressBar widthPercentage={progress} />}
                  {page}
                </Slide>
              </ThemedFonts>
            );
          })}
        </Slider>
      </Container>
    </ThemeProvider>
  );
};

export default InquiryFlowPreview;
